import { useQuery } from '@tanstack/react-query'
import { Col, Flex, Row } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { organizationsOrganizationsRetrieveOptions } from 'src/api/main'
import { Loading, QueryErrorAlert, SectionLabel } from 'src/components'
import { useFeatureTelemetry } from 'src/flags'
import { useHasWorkspacePermissions, useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { getWorkspaceDemoState, WorkspacePermissionCode } from 'src/utils'

import { OverviewActivity } from './OverviewActivity'
import { OverviewAppsHealth } from './OverviewAppsHealth'
import { OverviewDetails } from './OverviewDetails'
import { OverviewHero } from './OverviewHero'
import { OverviewPlanUsage } from './OverviewPlanUsage'
import { OverviewSecuritySignals } from './OverviewSecuritySignals'
import { OverviewSignInChart } from './OverviewSignInChart'

// Panels that read another section's data and link into it. Without that
// section's code the reads 403 and the links dead-end, so the panel is dropped.
const PANEL_CODES = [
  WorkspacePermissionCode.AUDIT_VIEW,
  WorkspacePermissionCode.APPLICATIONS_VIEW,
  WorkspacePermissionCode.USERS_VIEW,
  WorkspacePermissionCode.ORGANIZATION_MANAGE
]

export const WorkspaceOverviewPage = () => {
  const { id = '' } = useParams<{ id: string }>()
  const { user } = useSessionContext()
  const { t } = useTranslation()
  // P1 scope gating: the static/analytics Overview cards are hidden until the
  // telemetry backend lands. Real, API-backed cards (activity, sign-in chart,
  // details) always render; the grid collapses to full width when OFF.
  const telemetryOn = useFeatureTelemetry()
  const [mayViewAudit, mayViewApps, mayViewMembers, mayManageWorkspace] =
    useHasWorkspacePermissions(PANEL_CODES)

  // Every stat tile links into a section; with none of them reachable the strip
  // is empty and its heading would label nothing.
  const hasStats = mayViewMembers || mayViewApps || mayManageWorkspace

  const workspaceId = useMemo(() => {
    const n = Number(id)

    return Number.isFinite(n) ? n : NaN
  }, [id])

  const idValid = id !== 'new' && Number.isFinite(workspaceId)

  const {
    data: organization,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({
      path: { id: workspaceId }
    }),
    enabled: idValid
  })

  if (!idValid) {
    return <Navigate to={paths.workspaces.root} replace />
  }

  if (isLoading && !organization) {
    return <Loading />
  }

  if (isError) {
    return <QueryErrorAlert onRetry={() => void refetch()} />
  }

  if (!organization) {
    return <Navigate to={paths.workspaces.root} replace />
  }

  const accessRole = user?.organizations?.find(
    (o) => o.id === workspaceId
  )?.access_role
  const demoState = getWorkspaceDemoState(organization)

  return (
    <Flex vertical gap={16}>
      <OverviewHero
        workspaceId={id}
        workspaceName={organization.name}
        workspaceDomain={organization.domain_name}
        createdAt={organization.date_created}
        photoUrl={organization.photo?.file ?? undefined}
        firstName={user?.user?.first_name ?? undefined}
        accessRole={accessRole ?? undefined}
        isDemo={demoState.isDemo}
        demoExpiresAt={demoState.expiresAt}
      />

      {hasStats ? (
        <Flex vertical gap={8}>
          <SectionLabel style={{ margin: 0 }}>
            {t('workspaceOverview.sections.stats')}
          </SectionLabel>
          <OverviewDetails workspace={organization} />
        </Flex>
      ) : null}

      {mayViewAudit ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={telemetryOn ? 14 : 24}>
            <OverviewActivity workspaceId={workspaceId} workspaceIdParam={id} />
          </Col>
          {telemetryOn && (
            <Col xs={24} lg={10}>
              <OverviewSecuritySignals workspaceIdParam={id} />
            </Col>
          )}
        </Row>
      ) : null}

      {mayViewAudit || telemetryOn ? (
        <Row gutter={[16, 16]}>
          {/* Sign-in activity is served by the audit endpoint, not by the
              workspace one — it follows `audit.view`. */}
          {mayViewAudit ? (
            <Col xs={24} lg={telemetryOn ? 16 : 24}>
              <OverviewSignInChart workspaceId={workspaceId} />
            </Col>
          ) : null}
          {telemetryOn && (
            <Col xs={24} lg={mayViewAudit ? 8 : 24}>
              <OverviewPlanUsage />
            </Col>
          )}
        </Row>
      ) : null}

      {telemetryOn && mayViewApps ? (
        <OverviewAppsHealth workspaceIdParam={id} />
      ) : null}
    </Flex>
  )
}
